// Neo's saved notes — the pure half: decoding the SSE record and the chat
// chip's wording (ADR 0226). Every Neo surface that streams a turn reads it.
//
//   memories  { changes }  — notes Neo saved, replaced or forgot.
//
// Decoded defensively, like the alarm records: a malformed entry is dropped,
// never rendered half-filled.

export type NeoMemoryAction = 'saved' | 'replaced' | 'forgotten';

/** A note Neo saved, replaced or forgot. Reported whatever the model's reply says. */
export interface NeoMemoryChange {
  action: NeoMemoryAction;
  id: string;
  content: string;
}

/** Fired on `window` when Neo changed the saved notes, so an open Profile refetches. */
export const NEO_MEMORIES_UPDATED_EVENT = 'nm:neo-memories-updated';

const MEMORY_ACTIONS: ReadonlySet<string> = new Set(['saved', 'replaced', 'forgotten']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** Decode a `memories` event (or the blocking route's `memories`). */
export function parseMemoryChanges(payload: unknown): NeoMemoryChange[] {
  const list = isRecord(payload) ? payload.changes : payload;
  const out: NeoMemoryChange[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw)) continue;
    const action = stringOf(raw.action);
    const id = stringOf(raw.id);
    const content = stringOf(raw.content);
    if (action === undefined || !MEMORY_ACTIONS.has(action)) continue;
    if (id === undefined || content === undefined) continue;
    out.push({ action: action as NeoMemoryAction, id, content });
  }
  return out;
}

const MEMORY_VERB: Readonly<Record<NeoMemoryAction, string>> = {
  saved: 'Remembered',
  replaced: 'Updated memory',
  forgotten: 'Forgot',
};

/** The chat chip for a memory change. */
export function memoryChip(change: NeoMemoryChange): string {
  return `✓ ${MEMORY_VERB[change.action]}: “${change.content}”`;
}
